import {PersonDto} from "../../../client/person/PersonApiClient";
import {useTranslation} from "react-i18next";
import {useClientLocator} from "../../../client/ApiClientLocator";
import {useState} from "react";
import {Stack} from "@mui/material";
import ConfirmationDialog from "../../../common/dialog/ConfirmationDialog";
import {PersonTranslKey} from "../PersonTranslKey";
import Button from "../../../common/button/Button";
import {useSnackbar} from "@dain/mui-components";
import {CommonTranslKey} from "../../../common/CommonTranslKey";
import {PersonActionStatus} from "./PersonActionStatus";
import PersonActionStatusChecker from "./PersonActionStatusChecker";

export enum RobAction {
    FORCE_SYNC_REZA,
    FORCE_SYNC_ROB,
    NO_ACTION,
    SUBSCRIBE,
    UNSUBSCRIBE
}

export interface PersonActionPanelProps {
    person: PersonDto;
    reloadPersonDetail: () => void;
    reloadPersonGrid: () => void;
}

export function PersonActionPanel(props: PersonActionPanelProps) {
    const {person, reloadPersonDetail, reloadPersonGrid} = props;

    const {t } = useTranslation();
    const snackbar = useSnackbar();
    const {personClient} = useClientLocator();

    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);
    const [confirmationDialogTitle, setConfirmationDialogTitle] = useState<string>();
    const [confirmationDialogMessage, setConfirmationDialogMessage] = useState<string>();
    const [confirmationDialogAction, setConfirmationDialogAction] = useState<RobAction>();

    const [activeRobAction, setActiveRobAction] = useState<RobAction>(RobAction.NO_ACTION);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    function canForceSyncWithRob(person: PersonDto): boolean {
        return !!person?.personAclDto?.canForceSyncWithRob;
    }

    function canSubscribeToUpdatesFromRob(person: PersonDto): boolean {
        return !!person?.personAclDto?.canSubscribeToUpdatesFromRob;
    }

    function canUnsubscribeToUpdatesFromRob(person: PersonDto): boolean {
        return !!person?.personAclDto?.canUnsubscribeToUpdatesFromRob;
    }

    function forceSyncWithRob(personId: number) {
        personClient.forceSyncWithRob(personId)
            .then(() => {
                snackbar.success(t(PersonTranslKey.FORCE_SYNC_ROB_REQUESTED));
                setActiveRobAction(RobAction.FORCE_SYNC_ROB);
            })
            .catch((err) => {
                snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err);
            });
    }

    function forceSyncRezaRepresentations(personId: number) {
        personClient.forceSyncRezaRepresentations(personId)
            .then(() => {
                snackbar.success(t(PersonTranslKey.FORCE_SYNC_REZA_REQUESTED));
            })
            .catch((err) => {
                snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err);
            });
    }

    function subscribeToUpdatesFromRob(personId: number): void {
        personClient.subscribeToUpdatesFromRob(personId)
            .then(() => {
                snackbar.success(t(PersonTranslKey.SUBSCRIBE_TO_UPDATES_FROM_ROB_REQUESTED));
                setActiveRobAction(RobAction.SUBSCRIBE);
            })
            .catch(err => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err));
    }

    function unsubscribeToUpdatesFromRob(personId: number): void {
        personClient.unsubscribeToUpdatesFromRob(personId)
            .then(() => {
                snackbar.success(t(PersonTranslKey.UNSUBSCRIBE_FROM_UPDATES_FROM_ISZR_REQUESTED));
                setActiveRobAction(RobAction.UNSUBSCRIBE);
            })
            .catch(err => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err));
    }

    const handleForceSyncAction = (person: PersonDto): void => {
        if (canForceSyncWithRob(person)) {
            setConfirmationDialogTitle(t(PersonTranslKey.FORCE_SYNC_ROB_CONFIRM_TITLE));
            setConfirmationDialogMessage(t(PersonTranslKey.FORCE_SYNC_ROB_CONFIRM_DIALOG_MESSAGE));
            setConfirmationDialogAction(RobAction.FORCE_SYNC_ROB);
            setConfirmationDialogOpen(true);
        }
    };

    const handleRezaForceSyncAction = (person: PersonDto): void => {
        if (PersonActionStatusChecker.canForceSyncRezaRepresentations(person)) {
            setConfirmationDialogTitle(t(PersonTranslKey.FORCE_SYNC_REZA_CONFIRM_TITLE));
            setConfirmationDialogMessage(t(PersonTranslKey.FORCE_SYNC_REZA_CONFIRM_DIALOG_MESSAGE));
            setConfirmationDialogAction(RobAction.FORCE_SYNC_REZA);
            setConfirmationDialogOpen(true);
        }
    };

    const handleSubscribeClick = (person: PersonDto): void => {
        if (canSubscribeToUpdatesFromRob(person)) {
            setConfirmationDialogTitle(t(PersonTranslKey.SUBSCRIBE_TO_UPDATES_FROM_CONFIRM_TITLE));
            setConfirmationDialogMessage(t(PersonTranslKey.SUBSCRIBE_TO_UPDATES_FROM_CONFIRM_DIALOG_MESSAGE));
            setConfirmationDialogAction(RobAction.SUBSCRIBE);
            setConfirmationDialogOpen(true);
        }
    };

    const handleUnsubscribeClick = (person: PersonDto): void => {
        if (canUnsubscribeToUpdatesFromRob(person)) {
            setConfirmationDialogTitle(t(PersonTranslKey.UNSUBSCRIBE_FROM_UPDATES_FROM_ISZR_CONFIRM_TITLE));
            setConfirmationDialogMessage(t(PersonTranslKey.UNSUBSCRIBE_FROM_UPDATES_FROM_ISZR_CONFIRM_DIALOG_MESSAGE));
            setConfirmationDialogAction(RobAction.UNSUBSCRIBE);
            setConfirmationDialogOpen(true);
        }
    };

    function handleConfirmAction(): void {
        if (!person || confirmationDialogAction === undefined) {
            return;
        }

        if (confirmationDialogAction === RobAction.FORCE_SYNC_ROB) {
            forceSyncWithRob(person.id);
        }

        if (confirmationDialogAction === RobAction.FORCE_SYNC_REZA) {
            forceSyncRezaRepresentations(person.id);
        }

        if (confirmationDialogAction === RobAction.SUBSCRIBE) {
            subscribeToUpdatesFromRob(person.id);
        }

        if (confirmationDialogAction === RobAction.UNSUBSCRIBE) {
            unsubscribeToUpdatesFromRob(person.id);
        }

        setConfirmationDialogOpen(false);
        setConfirmationDialogAction(undefined);
    }

    function handleCancel(): void {
        setConfirmationDialogOpen(false);
        setConfirmationDialogAction(undefined);
    }

    function onActiveRobActionSuccess(): void {
        reloadPersonDetail();
        reloadPersonGrid();
    }

    return (
        <>
            <Stack direction="row" justifyContent="flex-end" spacing={1} padding={1}>
                <Button
                    variant="primary"
                    size="m"
                    tooltip={t(PersonTranslKey.FORCE_SYNC_ROB_TOOLTIP)}
                    disabled={isLoading || !canForceSyncWithRob(person)}
                    onClick={() => handleForceSyncAction(person)}>

                    {t(PersonTranslKey.FORCE_SYNC_ROB_CONFIRM_BUTTON)}
                </Button>

                <Button
                    variant="primary"
                    size="m"
                    tooltip={t(PersonTranslKey.FORCE_SYNC_REZA_TOOLTIP)}
                    disabled={isLoading || !PersonActionStatusChecker.canForceSyncRezaRepresentations(person)}
                    onClick={() => handleRezaForceSyncAction(person)}>

                    {t(PersonTranslKey.FORCE_SYNC_REZA_CONFIRM_BUTTON)}
                </Button>

                <Button
                    variant="primary"
                    size="m"
                    tooltip={t(PersonTranslKey.SUBSCRIBE_TO_UPDATES_FROM_ROB_TOOLTIP)}
                    disabled={isLoading || !canSubscribeToUpdatesFromRob(person)}
                    onClick={() => handleSubscribeClick(person)}>

                    {t(PersonTranslKey.SUBSCRIBE_TO_UPDATES_FROM_CONFIRM_BUTTON)}
                </Button>

                <Button
                    variant="primary"
                    size="m"
                    tooltip={t(PersonTranslKey.UNSUBSCRIBE_FROM_UPDATES_FROM_ISZR_TOOLTIP)}
                    disabled={isLoading || !canUnsubscribeToUpdatesFromRob(person)}
                    onClick={() => handleUnsubscribeClick(person)}>

                    {t(PersonTranslKey.UNSUBSCRIBE_FROM_UPDATES_FROM_ISZR_CONFIRM_BUTTON)}
                </Button>
            </Stack>

            <ConfirmationDialog open={confirmationDialogOpen}
                                title={confirmationDialogTitle!}
                                message={confirmationDialogMessage!}
                                onConfirmation={handleConfirmAction}
                                onCancel={handleCancel} />

            {person && activeRobAction !== RobAction.NO_ACTION &&

                <PersonActionStatus person={person}
                                    activeRobAction={activeRobAction}
                                    setActiveRobAction={setActiveRobAction}
                                    setIsLoading={setIsLoading}
                                    onActionComplete={onActiveRobActionSuccess} />
            }
        </>
    );
}
